import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import apiClient from '@services/api/client'

/**
 * Store pour gérer les permissions de l'utilisateur
 */
export const usePermissionsStore = create(
  devtools(
    persist(
      (set, get) => ({
        // État
        permissions: [],
        roles: [],
        isLoading: false,
        
        // Actions
        setPermissions: (permissions) => set({ permissions }),
        
        setRoles: (roles) => set({ roles }),
        
        /**
         * Vérifie si l'utilisateur a une permission spécifique
         * @param {string} permission - Code de la permission
         * @returns {boolean}
         */
        hasPermission: (permission) => {
          const { permissions } = get()
          return permissions.includes(permission)
        },
        
        /**
         * Vérifie si l'utilisateur a toutes les permissions spécifiées
         * @param {string[]} requiredPermissions - Liste des permissions requises
         * @returns {boolean}
         */
        hasAllPermissions: (requiredPermissions) => {
          const { permissions } = get()
          return requiredPermissions.every(perm => permissions.includes(perm))
        },
        
        /**
         * Vérifie si l'utilisateur a au moins une des permissions spécifiées
         * @param {string[]} requiredPermissions - Liste des permissions
         * @returns {boolean}
         */
        hasAnyPermission: (requiredPermissions) => {
          const { permissions } = get()
          return requiredPermissions.some(perm => permissions.includes(perm))
        },
        
        /**
         * Vérifie si l'utilisateur a un rôle spécifique
         * @param {string} role - Nom du rôle
         * @returns {boolean}
         */
        hasRole: (role) => {
          const { roles } = get()
          return roles.some(r => r.toLowerCase() === role.toLowerCase())
        },
        
        /**
         * Vérifie si l'utilisateur a l'un des rôles spécifiés
         * @param {string[]} rolesList - Liste des rôles
         * @returns {boolean}
         */
        hasAnyRole: (rolesList) => {
          const { roles } = get()
          return rolesList.some(role => 
            roles.some(r => r.toLowerCase() === role.toLowerCase())
          )
        },
        
        /**
         * Vérifie si l'utilisateur est Super Admin
         * @returns {boolean}
         */
        isSuperAdmin: () => {
          const { roles } = get()
          return roles.some(r => r.toLowerCase() === 'super admin')
        },
        
        /**
         * Vérifie si l'utilisateur est Technicien
         * @returns {boolean}
         */
        isTechnicien: () => {
          const { roles } = get()
          return roles.some(r => r.toLowerCase() === 'technicien')
        },
        
        /**
         * Vérifie si l'utilisateur peut accéder au contrôle système
         * @returns {boolean}
         */
        canAccessSystemControl: () => {
          const { isSuperAdmin, isTechnicien } = get()
          return isSuperAdmin() || isTechnicien()
        },
        
        /**
         * Réinitialise toutes les permissions
         */
        clearPermissions: () => set({ permissions: [], roles: [] }),
        
        /**
         * Charge les permissions depuis l'API
         */
        loadPermissions: async () => {
          set({ isLoading: true })
          try {
            const result = await apiClient.get('/profile')
            const profileData = result.user || result.data || {}
            set({
              permissions: profileData.permissions || [],
              roles: profileData.roles || [],
              isLoading: false
            })
          } catch (error) {
            console.error('Erreur lors du chargement des permissions:', error)
            set({ permissions: [], roles: [], isLoading: false })
          }
        },
      }),
      {
        name: 'permissions-storage',
        partialize: (state) => ({
          permissions: state.permissions,
          roles: state.roles,
        }),
      }
    ),
    {
      name: 'PermissionsStore',
    }
  )
)

export default usePermissionsStore
